import { Button, Center, Flex, HStack, Link, Spinner, Text, VStack } from "@chakra-ui/react"
import React, { useMemo } from "react"
import { NavLink } from "react-router-dom"
import { ExtendedUserFragment, MeUserFragment, useMeQuery, UserRoleTypes, useUserPermissionsGroupsQuery } from "../../graphql"
import { checkForPermissions } from "../../utils"
import { Card, Stat } from "../common"

export type UserPermissionsGroupsProps = {
	user: ExtendedUserFragment | MeUserFragment
}

export const UserPermissionsGroups: React.FC<UserPermissionsGroupsProps> = ({ user }) => {
	const [{ data, fetching, error }] = useUserPermissionsGroupsQuery({ variables: { userId: user._id } })

	const [{ data: meData }] = useMeQuery()

	const canUpdate = useMemo(() => {
		if (user.isBlacklisted) return false

		if (user.type.roleType === UserRoleTypes.Admin) {
			return checkForPermissions(meData?.me, "permissions/update-admin-user-permissions-group")[0]
		}

		return checkForPermissions(meData?.me, "permissions/update-user-permissions-group")[0]
	}, [meData, user])

	return (
		<Card
			title="Permissions Groups"
			action={
				canUpdate && (
					<Button as={NavLink} to={`/users/${user._id}/updatePermissionsGroups`} colorScheme="primary" size="sm">
						Update
					</Button>
				)
			}
		>
			{fetching ? (
				<Center w="full" py="4">
					<VStack w="full" color="grayscale.label">
						<Text fontSize="sm">Loading permissions groups</Text>
						<Spinner size="sm" />
					</VStack>
				</Center>
			) : error ? (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						{error.message.replace("[GraphQL] ", "")}
					</Text>
				</Center>
			) : data?.userPermissionsGroups ? (
				<Flex justify="center" align="center" flexWrap="wrap">
					<Stat
						label="Groups"
						value={
							data.userPermissionsGroups.length ? (
								<HStack flexWrap="wrap" justify="center" maxW="xs">
									{data.userPermissionsGroups.map((g, i) => (
										<Link key={g._id} as={NavLink} to={`/permissionsGroups/${g._id}`}>
											{g.label.name}
											{i < (data.userPermissionsGroups?.length || 0) - 1 && ","}
										</Link>
									))}
								</HStack>
							) : (
								"Not available"
							)
						}
					/>
				</Flex>
			) : (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						Couldn&apos;t find permissions groups.
					</Text>
				</Center>
			)}
		</Card>
	)
}
